import React, { useEffect, useState } from "react";

import { useGetPracticeSeedings, useTranslation } from "utils";

import { ZERO_UUID } from "models";

import { Flex, Form, OptGroup, Option, Select, Spin } from "components";

type MissingSeedingFormProps = {
  seedingTypes: string[];
  disabled: boolean;
  // perennials need no seeding in a given year, so "none" (the ZERO_UUID practice, diesel 0) is offered for them
  allowNone?: boolean;
};

export const MissingSeedingForm = ({ seedingTypes, disabled, allowNone = false }: MissingSeedingFormProps) => {
  const { t } = useTranslation("allFieldView");
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const form = Form.useFormInstance();

  const [selectedSeedingType, setSelectedSeedingType] = useState<string | null>(null);
  const [allSameSeeding, setAllSameSeeding] = useState<boolean>(false);

  useEffect(() => {
    const allSameSeeding = seedingTypes.every((seedingType, _, array) => seedingType === array[0]);
    setAllSameSeeding(allSameSeeding);
    if (!seedingTypes.find((seedingType) => seedingType != null && seedingType.trim() !== "")) {
      return;
    }
    if (allSameSeeding && seedingTypes.length > 0) {
      const seedingPracticeType = seedingTypes[0];
      const seedingType = (seedingPracticeType === ZERO_UUID && !allowNone ? t("fieldTimeline:cropCard.unknown") : seedingPracticeType) || null;
      setSelectedSeedingType(seedingType);
    }
  }, [seedingTypes, selectedSeedingType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // We need to set the form value for the seedingPracticeType whenever selectedSeedingType changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.

    form.setFieldsValue({ seedingPracticeType: selectedSeedingType });
  }, [selectedSeedingType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="add-edit-seeding-form" className="margin-top-200">
      {seedingPracticesQuery.isFetching ? (
        <Flex justify="center">
          <Spin />
        </Flex>
      ) : (
        <div>
          <Select
            dataTestId="add-edit-crop-form-seeding-type-selection"
            initialValue={selectedSeedingType}
            label={allSameSeeding ? t("cropForm.seedingType") : t("cropForm.multipleValues")}
            // label={<>{t("cropForm.seedingType")}</>}
            optionFilterProp="children"
            name={["seedingPracticeType"]}
            labelRender={({ label, value }) => (value === ZERO_UUID ? t("practices:seedings.none") : label)}
            showSearch
            value={selectedSeedingType}
            disabled={disabled}
          >
            <OptGroup>
              {seedingPracticesQuery.data &&
                seedingPracticesQuery.data
                  .filter((practice) => allowNone || practice.id !== ZERO_UUID)
                  .sort((a, b) => a.name?.localeCompare(b.name))
                  .map((option, index) => (
                    <Option data-testid={`seeding-selection-option-${index}`} key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
            </OptGroup>
          </Select>
        </div>
      )}
    </div>
  );
};
